/**
 * Ready-firms catalog (port of ready_firms.js without jQuery/Chosen) and the
 * archive "load more" button (loadmore.js). Filtering navigates with GET
 * parameters; the next page of results is fetched as HTML rows (?partial=rows).
 */
import { chosen } from './chosen'

const form = () => document.getElementById('firmsFilterForm') as HTMLFormElement | null

/** GET query of the form, empty values and service fields left out (as submitHandler did). */
function formQuery(f: HTMLFormElement) {
  const q = new URLSearchParams()
  for (const [k, v] of new FormData(f).entries()) {
    if (typeof v !== 'string' || !v || k === 'post_type' || k === 'page' || k === 'pagenum') continue
    q.append(k, v)
  }
  return q
}

function applyFilters(f: HTMLFormElement) {
  const q = formQuery(f).toString()
  location.href = location.pathname + (q ? `?${q}` : '')
}

async function loadMore(button: HTMLButtonElement) {
  const next = Number(button.dataset.pagenum ?? 1) + 1
  const q = new URLSearchParams(location.search)
  q.set('pagenum', String(next))
  q.set('partial', 'rows')
  button.disabled = true
  const res = await fetch(`${location.pathname}?${q}`)
  button.disabled = false
  if (!res.ok) return
  document.querySelector('[data-firm-list]')?.insertAdjacentHTML('beforeend', await res.text())
  button.dataset.pagenum = String(next)
  const pages = Number(res.headers.get('x-pages') ?? next)
  if (next >= pages) (button.closest('[data-more]') as HTMLElement).hidden = true
  const url = new URL(location.href)
  url.searchParams.set('pagenum', String(next))
  history.pushState({}, document.title, url)
}

async function quickView(link: HTMLElement) {
  const info = link.parentElement?.querySelector<HTMLElement>('.firm-hover-info')
  const body = info?.querySelector<HTMLElement>('.fhi-body')
  if (!info || !body) return
  link.parentElement!.style.position = 'relative'
  document.querySelectorAll<HTMLElement>('.firm-hover-info').forEach((i) => (i.style.display = ''))
  info.classList.add('firm-hover-info-fixed')
  document.body.classList.add('quick-view-open')
  body.scrollTop = 0
  if (body.innerHTML.trim()) return
  try {
    const data = await (await fetch(`/wp-json/nne/v1/firms/${link.dataset.code}/`)).json()
    body.innerHTML = data?.item
      ? `<div class="js-cb-content">${data.item.preview_text}</div><button data-action="copy_to_clipboard" type="button" class="btn-copy-to-cb js-copy2cb"></button>`
      : "Для просмотра нажмите 'подробнее'"
  } catch {
    body.innerHTML = "Для просмотра нажмите 'подробнее'"
  }
  body.classList.remove('loading-state')
}

function closeQuickView() {
  document.querySelectorAll('.firm-hover-info-fixed').forEach((i) => i.classList.remove('firm-hover-info-fixed'))
  document.body.classList.remove('quick-view-open')
}

async function copyInfo(btn: HTMLElement) {
  const text = btn.parentElement?.querySelector('.js-cb-content')?.textContent ?? ''
  try {
    await navigator.clipboard.writeText(text)
    btn.classList.add('success')
    window.setTimeout(() => btn.classList.remove('success'), 2000)
  } catch {}
}

/** Archive pages: "Показать ещё фирмы" appends the next page of rows. */
async function archiveLoadMore(button: HTMLButtonElement) {
  const next = Number(button.dataset.page ?? 1) + 1
  button.textContent = 'Загрузка...'
  const res = await fetch(`${button.dataset.base}page/${next}/?partial=rows`)
  if (!res.ok) return (button.closest('.more') ?? button).remove()
  document.querySelector('[data-archive-list]')?.insertAdjacentHTML('beforeend', await res.text())
  button.textContent = 'Показать ещё'
  button.dataset.page = String(next)
  if (next >= Number(button.dataset.pages)) (button.closest('.more') ?? button).remove()
}

export function initCatalog() {
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeQuickView()
  })
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement
    const more = t.closest<HTMLButtonElement>('.btn-load-ajax')
    if (more) return void loadMore(more)
    const archive = t.closest<HTMLButtonElement>('button[data-archive-more]')
    if (archive) return void archiveLoadMore(archive)
    const qv = t.closest<HTMLElement>('[data-action="quick_view"]')
    if (qv) {
      e.preventDefault()
      return void quickView(qv)
    }
    if (t.closest('.js-copy2cb')) return void copyInfo(t.closest<HTMLElement>('.js-copy2cb')!)
    if (t.classList.contains('firm-hover-info') || t.closest('[data-action="close-quick"]')) closeQuickView()
    const remove = t.closest<HTMLElement>('[data-action="remove-filter"]')
    const f = form()
    if (remove && f) {
      const select = f.querySelector<HTMLSelectElement>(`#${remove.dataset.id}`)
      if (select) {
        Array.from(select.options).forEach((o) => {
          if (!remove.dataset.value || o.value === remove.dataset.value) o.selected = false
        })
        applyFilters(f)
      }
    }
  })

  const f = form()
  if (!f) return
  // WordPress ran Chosen on every select of the form, single ones included.
  f.querySelectorAll<HTMLSelectElement>('select').forEach((s) => {
    chosen(s)
    s.addEventListener('change', () => applyFilters(f))
  })
  f.addEventListener('submit', (e) => {
    e.preventDefault()
    applyFilters(f)
  })
}
